/**
 * PriceRangeSlider Component for v0.7.0
 * Dual-handle price range slider for advanced search
 * [UPDATED v0.7.2] Added custom range slider implementation
 */

import { useState, useRef, useEffect } from 'react';
import './PriceRangeSlider.css';

export interface PriceRange {
  min: number | null;
  max: number | null;
}

interface PriceRangeSliderProps {
  value: PriceRange;
  onChange: (value: PriceRange) => void;
  min?: number;
  max?: number;
  step?: number;
  currency?: string;
}

const PriceRangeSlider = ({
  value,
  onChange,
  min = 0,
  max = 20000,
  step = 100,
  currency = 'NT$',
}: PriceRangeSliderProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<'min' | 'max' | null>(null);
  const [currentMin, setCurrentMin] = useState(value.min ?? min);
  const [currentMax, setCurrentMax] = useState(value.max ?? max);

  // Update current values when prop changes
  useEffect(() => {
    setCurrentMin(value.min ?? min);
    setCurrentMax(value.max ?? max);
  }, [value, min, max]);

  const handleMouseDown = (handle: 'min' | 'max') => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(handle);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newValue = min + percentage * (max - min);
    const roundedValue = Math.round(newValue / step) * step;

    if (isDragging === 'min') {
      const newMin = Math.min(roundedValue, currentMax - step);
      setCurrentMin(newMin);
    } else {
      const newMax = Math.max(roundedValue, currentMin + step);
      setCurrentMax(newMax);
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      onChange({
        min: currentMin === min ? null : currentMin,
        max: currentMax === max ? null : currentMax,
      });
    }
    setIsDragging(null);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, currentMin, currentMax]);

  // Touch support
  const handleTouchStart = (handle: 'min' | 'max') => (e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(handle);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newValue = min + percentage * (max - min);
    const roundedValue = Math.round(newValue / step) * step;

    if (isDragging === 'min') {
      const newMin = Math.min(roundedValue, currentMax - step);
      setCurrentMin(newMin);
    } else {
      const newMax = Math.max(roundedValue, currentMin + step);
      setCurrentMax(newMax);
    }
  };

  const handleTouchEnd = () => {
    if (isDragging) {
      onChange({
        min: currentMin === min ? null : currentMin,
        max: currentMax === max ? null : currentMax,
      });
    }
    setIsDragging(null);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      return () => {
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, currentMin, currentMax]);

  const minPercentage = ((currentMin - min) / (max - min)) * 100;
  const maxPercentage = ((currentMax - min) / (max - min)) * 100;

  return (
    <div className="price-range-slider">
      <div className="price-range-slider__labels">
        <span className="price-range-slider__label">
          {currentMin === min ? '不限' : `${currency} ${currentMin.toLocaleString()}`}
        </span>
        <span className="price-range-slider__label">
          {currentMax === max ? '不限' : `${currency} ${currentMax.toLocaleString()}`}
        </span>
      </div>

      <div
        ref={sliderRef}
        className="price-range-slider__track"
      >
        {/* Track fill */}
        <div
          className="price-range-slider__fill"
          style={{
            left: `${minPercentage}%`,
            right: `${100 - maxPercentage}%`,
          }}
        />

        {/* Min handle */}
        <div
          className="price-range-slider__handle price-range-slider__handle--min"
          style={{ left: `${minPercentage}%` }}
          onMouseDown={handleMouseDown('min')}
          onTouchStart={handleTouchStart('min')}
          role="slider"
          aria-label="最低價格"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={currentMin}
          tabIndex={0}
        />

        {/* Max handle */}
        <div
          className="price-range-slider__handle price-range-slider__handle--max"
          style={{ left: `${maxPercentage}%` }}
          onMouseDown={handleMouseDown('max')}
          onTouchStart={handleTouchStart('max')}
          role="slider"
          aria-label="最高價格"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={currentMax}
          tabIndex={0}
        />
      </div>

      <div className="price-range-slider__values">
        <input
          type="number"
          className="price-range-slider__input"
          value={currentMin === min ? '' : currentMin}
          onChange={(e) => {
            const newValue = e.target.value ? parseInt(e.target.value) : min;
            const validValue = Math.max(min, Math.min(newValue, currentMax - step));
            setCurrentMin(validValue);
            onChange({
              min: validValue === min ? null : validValue,
              max: currentMax === max ? null : currentMax,
            });
          }}
          placeholder="最低"
          min={min}
          max={max}
          step={step}
        />
        <span className="price-range-slider__separator">-</span>
        <input
          type="number"
          className="price-range-slider__input"
          value={currentMax === max ? '' : currentMax}
          onChange={(e) => {
            const newValue = e.target.value ? parseInt(e.target.value) : max;
            const validValue = Math.max(currentMin + step, Math.min(newValue, max));
            setCurrentMax(validValue);
            onChange({
              min: currentMin === min ? null : currentMin,
              max: validValue === max ? null : validValue,
            });
          }}
          placeholder="最高"
          min={min}
          max={max}
          step={step}
        />
      </div>
    </div>
  );
};

export default PriceRangeSlider;
